import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
  Nav,
  NavItem,
  NavLink,
  FormFeedback,
  Spinner,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { Triangle } from "react-loader-spinner";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import AdsService from "../../../services/OrderingServices/AdsService";
import AdvertisersService from "../../../services/OrderingServices/AdvertisersService";
import AdsTagsService from "../../../services/OrderingServices/AdsTagsService";
import DropzoneComponent from "./DropzoneComponent";
import ImageCropModal from "./ImageCropModal";
import Select from "react-select";

/* ─── Validation schemas ─── */
const imageSchema = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  description: Yup.string(),
  priority: Yup.number().typeError("Priority must be a number"),
  isActive: Yup.boolean(),
  advertiserId: Yup.number()
    .required("Advertiser is required")
    .typeError("Advertiser must be selected"),
  durationSeconds: Yup.number().typeError("Duration must be a number"),
  targetAllDevices: Yup.boolean(),
  tags: Yup.array(),
});

const videoSchema = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  description: Yup.string(),
  priority: Yup.number().typeError("Priority must be a number"),
  isActive: Yup.boolean(),
  advertiserId: Yup.number()
    .required("Advertiser is required")
    .typeError("Advertiser must be selected"),
  videoLengthSeconds: Yup.number().typeError("Video length must be a number"),
  targetAllDevices: Yup.boolean(),
  adFormat: Yup.number().required("Ad format is required"),
  tags: Yup.array(),
});

/* ═══════════════════════════════════
   Main Component
═══════════════════════════════════ */
const AdsForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [advertisers, setAdvertisers] = useState([]);
  const [activeTab, setActiveTab] = useState("image");
  const [mediaFiles, setMediaFiles] = useState([]);
  const [thumbnailFiles, setThumbnailFiles] = useState([]);
  const [currentTag, setCurrentTag] = useState("");
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingTags, setSearchingTags] = useState(false);
  const debounceTimeoutRef = useRef(null);
  const inputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [capturedFrame, setCapturedFrame] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [selectedTime, setSelectedTime] = useState(0);
  const [generatedThumbnail, setGeneratedThumbnail] = useState(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [hasScrubbed, setHasScrubbed] = useState(false);
  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      priority: 0,
      isActive: true,
      advertiserId: "",
      durationSeconds: 0,
      videoLengthSeconds: 0,
      targetAllDevices: false,
      adFormat: 1,
      tags: [],
    },
    enableReinitialize: true,
    validationSchema: activeTab === "image" ? imageSchema : videoSchema,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    fetchAdvertisers();
    if (id) fetchAdDetail();

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [id]);

  async function fetchAdvertisers() {
    try {
      const response = await AdvertisersService.advertisersList({
        pageSize: 100,
      });
      const advertisersList = Array.isArray(response)
        ? response
        : response.data || [];
      setAdvertisers(advertisersList);
    } catch (error) {
      console.error("Failed to load advertisers:", error);
      toast.error("Failed to load advertisers");
    }
  }

  async function fetchAdDetail() {
    try {
      setLoading(true);
      const response = await AdsService.getAdById(id);
      if (response) {
        formik.setValues({
          title: response.title || "",
          description: response.description || "",
          priority: response.priority || 0,
          isActive: response.isActive ?? false,
          targetAllDevices: response.targetAllDevices ?? false,
          advertiserId: response.advertiserId || "",
          durationSeconds: response.durationSeconds || 0,
          videoLengthSeconds: response.videoLengthSeconds || 0,
          adFormat: response.adFormat || 2,
          tags: Array.isArray(response.tags) ? response.tags : [],
        });
        if (response.mediaFileUrl) {
          setMediaFiles([{ isExisting: true, preview: response.mediaFileUrl }]);
        }
        if (response.thumbnailFileUrl) {
          setThumbnailFiles([
            { isExisting: true, preview: response.thumbnailFileUrl },
          ]);
        }
        if (response.videoLengthSeconds) setActiveTab("video");
      }
    } catch {
      toast.error("Failed to load ad details");
    } finally {
      setLoading(false);
    }
  }

  async function searchTagsSuggestions(searchTerm) {
    if (!searchTerm || searchTerm.trim().length === 0) {
      setTagSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setSearchingTags(true);

    try {
      const results = await AdsTagsService.searchTags(searchTerm);

      const filteredResults = Array.isArray(results)
        ? results.filter(
            (tag) =>
              !formik.values.tags.includes(tag.name) &&
              tag.name.toLowerCase().includes(searchTerm.toLowerCase()),
          )
        : [];

      setTagSuggestions(filteredResults);
      setShowSuggestions(filteredResults.length > 0);
    } catch (error) {
      console.error("Error searching tags:", error);
      setTagSuggestions([]);
    } finally {
      setSearchingTags(false);
    }
  }

  function handleTagInputChange(e) {
    let value = e.target.value;

    // remove spaces
    value = value.replace(/\s/g, "");

    setCurrentTag(value);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      searchTagsSuggestions(value);
    }, 300);
  }
  function addTagFromSuggestion(tag) {
    if (!formik.values.tags.includes(tag.name)) {
      formik.setFieldValue("tags", [...formik.values.tags, tag.name]);
    }

    setCurrentTag("");
    setTagSuggestions([]);
    setShowSuggestions(false);
    inputRef.current?.focus();
  }

  const captureFrame = (time) => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) return;

    video.currentTime = time;

    const handleSeeked = () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const previewUrl = URL.createObjectURL(blob);
            setCapturedFrame({
              url: previewUrl,
              file: blob,
            });
          }
        },
        "image/jpeg",
        0.9,
      );

      video.removeEventListener("seeked", handleSeeked);
    };

    video.addEventListener("seeked", handleSeeked);
  };

  const handleCropComplete = (croppedImage) => {
    setGeneratedThumbnail(croppedImage.url);
    setThumbnailFiles([
      {
        payload: croppedImage.file,
        preview: croppedImage.url,
      },
    ]);
    setCropModalOpen(false);
  };

  async function handleSubmit(values) {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("Title", values.title);
      formData.append("Description", values.description);
      formData.append("IsActive", values.isActive);
      formData.append("AdvertiserId", values.advertiserId);
      formData.append("TargetAllDevices", values.targetAllDevices);
      formData.append("AdFormat", values.adFormat);
      formData.append("AdType", activeTab);

      if (Array.isArray(values.tags) && values.tags.length > 0) {
        values.tags.forEach((tag, index) => {
          formData.append(`Tags[${index}]`, tag);
        });
      }

      if (activeTab === "image") {
        formData.append("DurationSeconds", values.durationSeconds);
        if (mediaFiles.length > 0 && mediaFiles[0].payload) {
          formData.append("MediaFile", mediaFiles[0].payload);
        }
      } else {
        formData.append("VideoLengthSeconds", values.videoLengthSeconds);
        if (mediaFiles.length > 0 && mediaFiles[0].payload) {
          formData.append("MediaFile", mediaFiles[0].payload);
        }
        if (thumbnailFiles.length > 0 && thumbnailFiles[0].payload) {
          formData.append("ThumbnailFile", thumbnailFiles[0].payload);
        }
      }

      if (id) {
        await AdsService.update(id, formData);
        toast.success("Ad updated successfully!");
      } else {
        await AdsService.create(formData);
        toast.success("Ad created successfully!");
      }
      navigate("/ads");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save ad");
    } finally {
      setLoading(false);
    }
  }

  if (loading && id) {
    return (
      <div className="d-flex justify-content-center mt-5">
        <Triangle color="#435ebe" height={80} width={80} />
      </div>
    );
  }

  const breadcrumbItems = [
    { title: "Back To List", onClick: () => navigate(-1) },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={id ? "Edit Ad" : "Create New Ad"}
            pageTitle="Ads Management"
            breadcrumbItems={breadcrumbItems}
          />

          <Row>
            <Col lg="12">
              <Card>
                {/* Tab Navigation */}
                <Nav tabs className="nav-tabs-custom">
                  <NavItem>
                    <NavLink
                      className={activeTab === "image" ? "active" : ""}
                      onClick={() => setActiveTab("image")}
                      style={{ cursor: "pointer" }}
                    >
                      Image Ad
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "video" ? "active" : ""}
                      onClick={() => setActiveTab("video")}
                      style={{ cursor: "pointer" }}
                    >
                      Video Ad
                    </NavLink>
                  </NavItem>
                  {id && (
                    <div className="ms-auto p-2">
                      <Badge color="info">Editing</Badge>
                    </div>
                  )}
                </Nav>

                <CardBody>
                  <Form onSubmit={formik.handleSubmit}>
                    <Row>
                      {/* Left Column - Basic Information */}
                      <Col md="4">
                        <h6 className="text-muted text-uppercase mb-3">
                          Basic Information
                        </h6>

                        {/* Title */}
                        <FormGroup>
                          <Label for="title">
                            Title <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="text"
                            name="title"
                            id="title"
                            placeholder="Enter ad title"
                            value={formik.values.title}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.title && !!formik.errors.title
                            }
                          />
                          {formik.touched.title && formik.errors.title && (
                            <FormFeedback>{formik.errors.title}</FormFeedback>
                          )}
                        </FormGroup>

                        {/* Tags */}
                        <FormGroup>
                          <Label for="tags">Tags</Label>
                          <div className="border rounded p-2 bg-light position-relative">
                            <div className="d-flex flex-wrap align-items-center gap-2">
                              {formik.values.tags.map((tag, idx) => (
                                <Badge
                                  key={idx}
                                  color="primary"
                                  className="cursor-pointer"
                                  onClick={() => {
                                    const newTags = formik.values.tags.filter(
                                      (_, i) => i !== idx,
                                    );
                                    formik.setFieldValue("tags", newTags);
                                  }}
                                >
                                  #{tag} &times;
                                </Badge>
                              ))}

                              <input
                                ref={inputRef}
                                type="text"
                                className="border-0 bg-transparent flex-grow-1"
                                value={currentTag}
                                onChange={handleTagInputChange}
                                onKeyDown={(e) => {
                                  if (e.key === " ") {
                                    e.preventDefault(); // 🚫 block space
                                    return;
                                  }

                                  if (e.key === "Enter") {
                                    e.preventDefault();

                                    const tag = currentTag.trim();
                                    if (
                                      tag &&
                                      !formik.values.tags.includes(tag)
                                    ) {
                                      formik.setFieldValue("tags", [
                                        ...formik.values.tags,
                                        tag,
                                      ]);
                                    }

                                    setCurrentTag("");
                                    setTagSuggestions([]);
                                    setShowSuggestions(false);
                                  }

                                  if (e.key === "Escape") {
                                    setShowSuggestions(false);
                                  }
                                }}
                              />
                            </div>

                            {showSuggestions && tagSuggestions.length > 0 && (
                              <div
                                className="position-absolute bg-white border rounded shadow-sm"
                                style={{
                                  top: "100%",
                                  left: 0,
                                  right: 0,
                                  maxHeight: "200px",
                                  overflowY: "auto",
                                  zIndex: 1000,
                                  marginTop: "4px",
                                }}
                              >
                                {tagSuggestions.map((suggestion, idx) => (
                                  <div
                                    key={suggestion.id}
                                    className="p-2 d-flex justify-content-between align-items-center cursor-pointer"
                                    onClick={() =>
                                      addTagFromSuggestion(suggestion)
                                    }
                                    onMouseEnter={(e) =>
                                      e.currentTarget.classList.add("bg-light")
                                    }
                                    onMouseLeave={(e) =>
                                      e.currentTarget.classList.remove(
                                        "bg-light",
                                      )
                                    }
                                  >
                                    <span style={{ fontWeight: 500 }}>
                                      #{suggestion.name}
                                    </span>

                                    <span
                                      style={{
                                        fontSize: "11px",
                                        color: "#6c757d",
                                        background: "#f1f3f5",
                                        padding: "2px 6px",
                                        borderRadius: "10px",
                                      }}
                                    >
                                      {suggestion.usageCount} uses
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {searchingTags && (
                              <div className="position-absolute bg-white border rounded p-2 small text-muted">
                                Searching...
                              </div>
                            )}
                          </div>
                        </FormGroup>

                        {/* Advertiser */}
                        <FormGroup>
                          <Label for="advertiserId">
                            Advertiser <span className="text-danger">*</span>
                          </Label>
                          <Select
                            name="advertiserId"
                            options={advertisers.map((a) => ({
                              value: a.id,
                              label: a.advertiserName,
                            }))}
                            value={advertisers
                              .map((a) => ({
                                value: a.id,
                                label: a.advertiserName,
                              }))
                              .find(
                                (opt) =>
                                  opt.value === formik.values.advertiserId,
                              )}
                            onChange={(selected) =>
                              formik.setFieldValue(
                                "advertiserId",
                                selected?.value,
                              )
                            }
                            onBlur={() =>
                              formik.setFieldTouched("advertiserId")
                            }
                            placeholder="Select advertiser..."
                          />
                          {formik.touched.advertiserId &&
                            formik.errors.advertiserId && (
                              <div className="text-danger small mt-1">
                                {formik.errors.advertiserId}
                              </div>
                            )}
                        </FormGroup>

                        <hr />

                        {/* Duration/Video Length based on tab */}
                        {activeTab === "image" ? (
                          <FormGroup>
                            <Label for="durationSeconds">
                              Duration (seconds)
                            </Label>
                            <Input
                              type="number"
                              name="durationSeconds"
                              id="durationSeconds"
                              placeholder="e.g. 30"
                              value={formik.values.durationSeconds}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              invalid={
                                formik.touched.durationSeconds &&
                                !!formik.errors.durationSeconds
                              }
                            />
                            {formik.touched.durationSeconds &&
                              formik.errors.durationSeconds && (
                                <FormFeedback>
                                  {formik.errors.durationSeconds}
                                </FormFeedback>
                              )}
                          </FormGroup>
                        ) : (
                          <>
                            <FormGroup>
                              <Label for="videoLengthSeconds">
                                Video Length (seconds)
                              </Label>
                              <Input
                                type="number"
                                name="videoLengthSeconds"
                                id="videoLengthSeconds"
                                placeholder="e.g. 60"
                                value={formik.values.videoLengthSeconds}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                invalid={
                                  formik.touched.videoLengthSeconds &&
                                  !!formik.errors.videoLengthSeconds
                                }
                              />
                              {formik.touched.videoLengthSeconds &&
                                formik.errors.videoLengthSeconds && (
                                  <FormFeedback>
                                    {formik.errors.videoLengthSeconds}
                                  </FormFeedback>
                                )}
                            </FormGroup>

                            <FormGroup>
                              <Label for="adFormat">
                                Ad Format <span className="text-danger">*</span>
                              </Label>
                              <Select
                                name="adFormat"
                                options={[
                                  { value: 1, label: "Full Screen" },
                                  { value: 2, label: "Banner" },
                                ]}
                                placeholder="Select Ad Format"
                                value={[
                                  { value: 1, label: "Full Screen" },
                                  { value: 2, label: "Banner" },
                                ].find(
                                  (option) =>
                                    option.value === formik.values.adFormat,
                                )}
                                onChange={(selected) =>
                                  formik.setFieldValue(
                                    "adFormat",
                                    selected?.value,
                                  )
                                }
                                onBlur={() =>
                                  formik.setFieldTouched("adFormat", true)
                                }
                              />
                              {formik.touched.adFormat &&
                                formik.errors.adFormat && (
                                  <FormFeedback>
                                    {formik.errors.adFormat}
                                  </FormFeedback>
                                )}
                            </FormGroup>
                          </>
                        )}

                        <hr />

                        {/* Settings */}
                        <h6 className="text-muted text-uppercase mb-3">
                          Settings
                        </h6>
                        <FormGroup check className="mb-3">
                          <Input
                            type="checkbox"
                            id="isActive"
                            checked={formik.values.isActive}
                            onChange={(e) =>
                              formik.setFieldValue("isActive", e.target.checked)
                            }
                          />
                          <Label check for="isActive">
                            Active
                          </Label>
                        </FormGroup>
                        <div>
                          <Button
                            color="primary"
                            type="submit"
                            disabled={loading}
                            className="me-2"
                          >
                            {loading ? (
                              <>
                                <Spinner size="sm" className="me-2" />
                                Saving...
                              </>
                            ) : id ? (
                              `Update ${
                                activeTab === "image" ? "Image" : "Video"
                              } Ad`
                            ) : (
                              `Create ${
                                activeTab === "image" ? "Image" : "Video"
                              } Ad`
                            )}
                          </Button>
                          <Button
                            color="secondary"
                            type="button"
                            onClick={() => navigate("/ads")}
                            disabled={loading}
                          >
                            Cancel
                          </Button>
                        </div>
                      </Col>

                      {/* Right Column - Media Files */}
                      <Col md="8">
                        <h6 className="text-muted text-uppercase mb-3">
                          Media Files
                        </h6>

                        {activeTab === "image" ? (
                          <FormGroup>
                            <Label>
                              Image File{" "}
                              <Badge color="secondary" className="ms-2">
                                JPG · PNG
                              </Badge>
                            </Label>
                            <DropzoneComponent
                              selectedFiles={mediaFiles}
                              setSelectedFiles={setMediaFiles}
                              fileType="image"
                            />
                          </FormGroup>
                        ) : (
                          <>
                            <Row>
                              <Col>
                                <FormGroup>
                                  <Label>
                                    Video File{" "}
                                    <Badge color="secondary" className="ms-2">
                                      MP4
                                    </Badge>
                                  </Label>
                                  <DropzoneComponent
                                    selectedFiles={mediaFiles}
                                    setSelectedFiles={setMediaFiles}
                                    fileType="video"
                                  />
                                </FormGroup>
                              </Col>
                              <Col>
                                {/* Scrubber Section */}
                                <div style={{ marginBottom: "12px" }}>
                                  {/* Time Display */}
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        color: "#495057",
                                      }}
                                    >
                                      Choose Thumbnail Frame
                                    </span>
                                    <span
                                      style={{
                                        fontSize: "12px",
                                        color: "#6c757d",
                                        fontWeight: "500",
                                      }}
                                    >
                                      {selectedTime.toFixed(1)}s /{" "}
                                      {videoDuration.toFixed(1)}s
                                    </span>
                                  </div>

                                  {/* Custom Range Slider */}
                                  <div style={{ position: "relative" }}>
                                    <Input
                                      type="range"
                                      min="0"
                                      max={videoDuration}
                                      step="0.1"
                                      value={selectedTime}
                                      onChange={(e) => {
                                        const time = parseFloat(e.target.value);
                                        setSelectedTime(time);
                                        setHasScrubbed(true);
                                        captureFrame(time);
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === "ArrowLeft") {
                                          e.preventDefault();
                                          const newTime = Math.max(
                                            0,
                                            selectedTime - 0.5,
                                          );
                                          setSelectedTime(newTime);
                                          setHasScrubbed(true);

                                          captureFrame(newTime);
                                        } else if (e.key === "ArrowRight") {
                                          e.preventDefault();
                                          const newTime = Math.min(
                                            videoDuration,
                                            selectedTime + 0.5,
                                          );
                                          setSelectedTime(newTime);
                                          setHasScrubbed(true);

                                          captureFrame(newTime);
                                        }
                                      }}
                                      style={{
                                        width: "100%",
                                        height: "8px",
                                        cursor: "pointer",
                                        background:
                                          "linear-gradient(to right, #e9ecef 0%, #e9ecef 100%)",
                                        borderRadius: "4px",
                                        outline: "none",
                                        WebkitAppearance: "none",
                                      }}
                                    />
                                    <style>{`
                                            input[type="range"]::-webkit-slider-thumb {
                                              -webkit-appearance: none;
                                              width: 18px;
                                              height: 18px;
                                              border-radius: 50%;
                                              background: linear-gradient(135deg, #435ebe 0%, #3d4fa3 100%);
                                              cursor: pointer;
                                              box-shadow: 0 2px 8px rgba(67, 94, 190, 0.4);
                                              border: 2px solid white;
                                            }
                                            input[type="range"]::-moz-range-thumb {
                                              width: 18px;
                                              height: 18px;
                                              border-radius: 50%;
                                              background: linear-gradient(135deg, #435ebe 0%, #3d4fa3 100%);
                                              cursor: pointer;
                                              box-shadow: 0 2px 8px rgba(67, 94, 190, 0.4);
                                              border: 2px solid white;
                                            }
                                          `}</style>
                                  </div>

                                  <small
                                    style={{
                                      display: "block",
                                      marginTop: "8px",
                                      fontSize: "11px",
                                      color: "#6c757d",
                                    }}
                                  >
                                    Drag slider for fine adjustments
                                  </small>
                                </div>

                                {/* Quick Time Presets */}
                                {/* <div
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {[0, 10, 25, 50, 75, 100].map((percent) => (
                                    <button
                                      key={percent}
                                      type="button"
                                      onClick={() => {
                                        const time =
                                          percent === 100
                                            ? videoDuration
                                            : (percent / 100) * videoDuration;
                                        setSelectedTime(time);
                                        captureFrame(time);
                                      }}
                                      style={{
                                        padding: "5px 12px",
                                        fontSize: "11px",
                                        fontWeight: "500",
                                        border: "1px solid #dee2e6",
                                        borderRadius: "4px",
                                        background: "#fff",
                                        color: "#495057",
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.borderColor = "#435ebe";
                                        e.target.style.color = "#435ebe";
                                        e.target.style.backgroundColor =
                                          "#f0f4ff";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.borderColor = "#dee2e6";
                                        e.target.style.color = "#495057";
                                        e.target.style.backgroundColor = "#fff";
                                      }}
                                    >
                                      {percent === 0
                                        ? "Start"
                                        : percent === 100
                                        ? "End"
                                        : `${percent}%`}
                                    </button>
                                  ))}
                                </div> */}
                                {mediaFiles.length > 0 && (
                                  <FormGroup>
                                    <div
                                      style={{
                                        backgroundColor: "#F8F9FA",
                                        borderRadius: "8px",
                                        padding: "16px",
                                        minHeight: hasScrubbed ? "100%" : "0px",
                                      }}
                                    >
                                      <div
                                        className="d-flex justify-content-center align-items-center"
                                        style={{
                                          display: "grid",
                                          gridTemplateColumns: "1fr 250px",

                                          alignItems: "start",
                                        }}
                                      >
                                        {/* LEFT: Video Player & Scrubber */}
                                        <div className="d-flex justify-content-center align-items-center">
                                          {/* Video Preview */}
                                          <div
                                            style={{
                                              marginBottom: "16px",
                                              borderRadius: "6px",
                                              overflow: "hidden",
                                              backgroundColor: "#000",
                                              aspectRatio: "16/9",
                                              display: "none",
                                            }}
                                          >
                                            <video
                                              ref={videoRef}
                                              src={mediaFiles[0].preview}
                                              controls
                                              style={{
                                                width: "100%",
                                                height: "100%",
                                                display: "block",
                                              }}
                                              onLoadedMetadata={(e) => {
                                                setVideoDuration(
                                                  e.target.duration,
                                                );
                                              }}
                                            />
                                          </div>
                                        </div>

                                        {/* RIGHT: Thumbnail Preview Panel */}
                                        <div className="w-100">
                                          <div
                                            style={{
                                              backgroundColor: "#fff",
                                              borderRadius: "6px",
                                              padding: "14px",
                                              border: "2px solid #e9ecef",
                                              minHeight: hasScrubbed
                                                ? "100%"
                                                : "0px",
                                              // maxHeight: "400px",
                                              display: "flex",
                                              flexDirection: "column",
                                              justifyContent: "space-between",
                                            }}
                                          >
                                            {/* Title */}
                                            <div
                                              style={{ marginBottom: "12px" }}
                                            >
                                              <div
                                                style={{
                                                  fontSize: "12px",
                                                  fontWeight: "700",
                                                  color: "#212529",
                                                  textTransform: "uppercase",
                                                  letterSpacing: "0.5px",
                                                }}
                                              >
                                                Thumbnail Preview
                                              </div>
                                              <div
                                                style={{
                                                  fontSize: "10px",
                                                  color: "#6c757d",
                                                  marginTop: "2px",
                                                }}
                                              >
                                                20:9 Aspect Ratio
                                              </div>
                                            </div>

                                            {/* Thumbnail Image */}
                                            {generatedThumbnail ? (
                                              <>
                                                <div
                                                  style={{
                                                    flex: 1,
                                                    minHeight: "140px",
                                                    borderRadius: "4px",
                                                    overflow: "hidden",
                                                    backgroundColor: "#f8f9fa",
                                                    border: "2px solid #28a745",
                                                    marginBottom: "12px",
                                                  }}
                                                >
                                                  <img
                                                    src={generatedThumbnail}
                                                    alt="Thumbnail Preview"
                                                    style={{
                                                      width: "100%",
                                                      height: "100%",
                                                      objectFit: "cover",
                                                    }}
                                                  />
                                                </div>

                                                <div
                                                  style={{
                                                    backgroundColor: "#d4edda",
                                                    borderRadius: "4px",
                                                    padding: "8px 12px",
                                                    textAlign: "center",
                                                    borderLeft:
                                                      "4px solid #28a745",
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      fontSize: "20px",
                                                      marginBottom: "2px",
                                                    }}
                                                  >
                                                    ✓
                                                  </div>
                                                  <div
                                                    style={{
                                                      fontSize: "11px",
                                                      fontWeight: "600",
                                                      color: "#155724",
                                                    }}
                                                  >
                                                    Ready to Upload
                                                  </div>
                                                  <div
                                                    style={{
                                                      fontSize: "10px",
                                                      marginTop: "2px",
                                                    }}
                                                  >
                                                    Captured @{" "}
                                                    {selectedTime.toFixed(1)}s
                                                  </div>
                                                </div>
                                              </>
                                            ) : (
                                              <>
                                                {capturedFrame ? (
                                                  <>
                                                    <div
                                                      style={{
                                                        flex: 1,
                                                        minHeight: "200px",
                                                        borderRadius: "4px",
                                                        overflow: "hidden",
                                                        backgroundColor:
                                                          "#f8f9fa",
                                                        border:
                                                          "2px solid #dee2e6",
                                                        marginBottom: "12px",
                                                      }}
                                                    >
                                                      <img
                                                        src={capturedFrame.url}
                                                        alt="Captured Frame"
                                                        style={{
                                                          width: "100%",
                                                          height: "100%",
                                                          objectFit: "contain",
                                                        }}
                                                      />
                                                    </div>

                                                    <button
                                                      type="button"
                                                      onClick={() => {
                                                        setImageToCrop(
                                                          capturedFrame.url,
                                                        );
                                                        setCropModalOpen(true);
                                                      }}
                                                      style={{
                                                        width: "100%",
                                                        padding: "8px",
                                                        fontSize: "12px",
                                                        fontWeight: "600",
                                                        borderRadius: "4px",
                                                        border:
                                                          "1px solid #435ebe",
                                                        backgroundColor:
                                                          "#435ebe",
                                                        color: "#fff",
                                                        cursor: "pointer",
                                                      }}
                                                    >
                                                      ✂ Crop Thumbnail
                                                    </button>
                                                  </>
                                                ) : (
                                                  <div
                                                    style={{
                                                      flex: 1,
                                                      display: "flex",
                                                      alignItems: "center",
                                                      justifyContent: "center",
                                                      minHeight: "140px",
                                                      backgroundColor:
                                                        "#f8f9fa",
                                                      borderRadius: "4px",
                                                      border:
                                                        "2px dashed #dee2e6",
                                                      color: "#6c757d",
                                                      textAlign: "center",
                                                      marginBottom: "12px",
                                                    }}
                                                  >
                                                    <div>
                                                      <div
                                                        style={{
                                                          fontSize: "32px",
                                                          marginBottom: "8px",
                                                        }}
                                                      >
                                                        🎬
                                                      </div>
                                                      <div
                                                        style={{
                                                          fontSize: "12px",
                                                          fontWeight: "500",
                                                        }}
                                                      >
                                                        Scrub the video
                                                        <br />
                                                        to generate thumbnail
                                                      </div>
                                                    </div>
                                                  </div>
                                                )}
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    <canvas
                                      ref={canvasRef}
                                      style={{ display: "none" }}
                                    />
                                  </FormGroup>
                                )}
                              </Col>
                            </Row>
                          </>
                        )}
                      </Col>
                    </Row>

                    {/* Action Buttons */}
                    {/* <Row className="mt-4 bg-danger">
                      <Col>
                        <Button
                          color="primary"
                          type="submit"
                          disabled={loading}
                          className="me-2"
                        >
                          {loading ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Saving...
                            </>
                          ) : id ? (
                            `Update ${
                              activeTab === "image" ? "Image" : "Video"
                            } Ad`
                          ) : (
                            `Create ${
                              activeTab === "image" ? "Image" : "Video"
                            } Ad`
                          )}
                        </Button>
                        <Button
                          color="secondary"
                          type="button"
                          onClick={() => navigate("/ads")}
                          disabled={loading}
                        >
                          Cancel
                        </Button>
                      </Col>
                    </Row> */}
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <ImageCropModal
        isOpen={cropModalOpen}
        toggle={() => setCropModalOpen(false)}
        imageSrc={imageToCrop}
        onCropComplete={handleCropComplete}
      />
    </React.Fragment>
  );
};

export default AdsForm;
